import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleAdminLogin } from '../../components/auth/GoogleAdminLogin';
import { useAuth } from '../../lib/authStore';
import { Shield, Lock, ArrowLeft, CheckCircle2 } from 'lucide-react';

export const AdminLoginPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // If already authenticated as admin, redirect directly
  if (isAuthenticated && user && (user.role === 'SUPER_ADMIN' || user.role === 'ADMIN' || user.role === 'EDITOR')) {
    navigate('/admin');
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Subtle Background Glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Main Container */}
      <div className="max-w-md w-full space-y-8 p-8 sm:p-10 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-2xl backdrop-blur-xl relative z-10">
        
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl overflow-hidden border border-slate-700 bg-slate-800 p-1 mx-auto shadow-inner flex items-center justify-center">
            <img
              src="/ftt-logo.png"
              alt="For The Truth Publishing"
              className="w-full h-full object-contain rounded-xl"
            />
          </div>

          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[11px] font-mono font-medium tracking-wide">
            <Shield className="w-3.5 h-3.5" />
            <span>EXECUTIVE &amp; EDITORIAL ACCESS</span>
          </div>

          <h1 className="font-extrabold text-2xl sm:text-3xl text-white tracking-tight">
            Admin Authentication
          </h1>
          <p className="text-xs text-slate-400 font-sans leading-relaxed">
            For The Truth Publishing administrative management console and editorial suite.
          </p>
        </div>

        {/* Security Notice */}
        <div className="p-3.5 rounded-2xl bg-slate-800/60 border border-slate-700/60 text-xs text-slate-300 space-y-2">
          <div className="flex items-center gap-2 text-slate-200 font-semibold text-xs">
            <Lock className="w-3.5 h-3.5 text-amber-400" />
            <span>Authorized Personnel Only</span>
          </div>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Administrative access is restricted to verified organizational Google accounts with pre-approved publishing security clearances.
          </p>
        </div>

        {/* Google 1-Click Login */}
        <div className="pt-2">
          <GoogleAdminLogin autoPrompt={true} />
        </div>

        {/* Whitelist Security Highlights */}
        <div className="pt-4 border-t border-slate-800 space-y-2.5">
          <span className="text-[10px] font-mono uppercase font-bold text-slate-500 block text-center tracking-wider">
            Protected Governance Controls
          </span>
          <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-400">
            <div className="flex items-center gap-1.5 p-2 rounded-lg bg-slate-800/40 border border-slate-800">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span>Multi-Tenant RBAC</span>
            </div>
            <div className="flex items-center gap-1.5 p-2 rounded-lg bg-slate-800/40 border border-slate-800">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span>S3 Vault Access</span>
            </div>
            <div className="flex items-center gap-1.5 p-2 rounded-lg bg-slate-800/40 border border-slate-800">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span>Editorial Approvals</span>
            </div>
            <div className="flex items-center gap-1.5 p-2 rounded-lg bg-slate-800/40 border border-slate-800">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span>Audit Log Trails</span>
            </div>
          </div>
        </div>

        {/* Return to Public Website */}
        <div className="text-center pt-2">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Return to For The Truth Publishing</span>
          </Link>
        </div>

      </div>
    </div>
  );
};
